import React, { cloneElement } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import noop from '../utils/noop';
import DropdownTrigger from './dropdown-trigger';
import DropdownContent from './dropdown-content';
import { subscribe, unsubscribe } from './event-emitter';
import { HIDE_DROPDOWN, OPEN_DROPDOWN } from './const';

class Dropdown extends React.Component {
  static dispatchHide() {
    const HIDE_DROPDOWN_EVENT = new Event(HIDE_DROPDOWN);
    document.dispatchEvent(HIDE_DROPDOWN_EVENT);
  }

  constructor(props) {
    super(props);
    this.state = {
      active: false,
    };
    this.bindMethods();
  }

  componentDidMount() {
    window.addEventListener('click', this.onWindowClick, false);
    window.addEventListener('touchstart', this.onWindowClick, false);
    window.addEventListener('blur', this.onWindowBlur, false);
    document.addEventListener(HIDE_DROPDOWN, this.hide);
    subscribe(OPEN_DROPDOWN, this.onShow);
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.onWindowClick, false);
    window.removeEventListener('touchstart', this.onWindowClick, false);
    window.removeEventListener('blur', this.onWindowBlur, false);
    document.removeEventListener(HIDE_DROPDOWN, this.hide);
    unsubscribe(OPEN_DROPDOWN, this.onShow);
    clearTimeout(this.hoverTimeout);
  }

  onToggleClick(event) {
    const { active } = this.state;
    event.preventDefault();

    if (active) {
      this.hide();
    } else {
      this.show();
    }
  }

  onWindowClick(event) {
    const { active } = this.state;
    const dropdownElement = this.dropdown;
    if (
      dropdownElement &&
      event.target !== dropdownElement &&
      !dropdownElement.contains(event.target) &&
      active
    ) {
      this.hide();
    }
  }

  onWindowBlur() {
    if (this.dropdown) {
      this.hide();
    }
  }

  onMouseEnter() {
    clearTimeout(this.hoverTimeout);

    this.show();
  }

  onMouseLeave() {
    const hideHoverDelay = 275;

    this.hoverTimeout = setTimeout(this.hide, hideHoverDelay);
  }

  onShow(labelFor) {
    const { id } = this.props;

    if (labelFor === id) {
      // since component listens window click event
      // and close all dropdowns
      // we should delay show action through setTimeout
      // to call it after close action
      setTimeout(this.show);
    }
  }

  show() {
    const { onShow, dropOn } = this.props;

    // Hide other dropdowns before show in order to avoid
    // cases when one of dropdown overlaps other dropdown
    if (dropOn === 'hover') {
      Dropdown.dispatchHide();
    }

    this.setState(
      {
        active: true,
      },
      onShow
    );
  }

  hide() {
    const { onHide } = this.props;
    this.setState(
      {
        active: false,
      },
      onHide
    );
  }

  bindMethods() {
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onWindowClick = this.onWindowClick.bind(this);
    this.onWindowBlur = this.onWindowBlur.bind(this);
    this.onToggleClick = this.onToggleClick.bind(this);
    this.boundChildren = this.boundChildren.bind(this);
    this.saveDropdownLink = this.saveDropdownLink.bind(this);
    this.hide = this.hide.bind(this);
    this.show = this.show.bind(this);
    this.onShow = this.onShow.bind(this);
  }

  boundChildren() {
    const { active } = this.state;
    const { children, disabled, removeElement, dropOn } = this.props;
    const showOnHover = dropOn === 'hover';

    const skipClickEvent = disabled || showOnHover;

    return children.map((item, index) => {
      if (item.type === DropdownTrigger) {
        const originalOnClick = item.props.onClick;
        return cloneElement(item, {
          key: `dropdown-item-${index}`,
          ...(!skipClickEvent && {
            onClick: (event) => {
              this.onToggleClick(event);
              if (originalOnClick) {
                originalOnClick.apply(item, arguments);
              }
            },
          }),
        });
      }
      if (item.type === DropdownContent && removeElement && !active) {
        return null;
      }

      return item;
    });
  }

  saveDropdownLink(dropdown) {
    this.dropdown = dropdown;
  }

  render() {
    const { className, drop, dropOn, disabled, ...otherProps } = this.props;
    const { active } = this.state;
    const showOnHover = dropOn === 'hover' && !disabled;
    const boundChildren = this.boundChildren();
    const dropdownClasses = classNames('ws-dropdown', `ws-dropdown--${drop}`, className, {
      'ws-dropdown--active': active,
    });
    const cleanProps = { ...otherProps };
    delete cleanProps.onShow;
    delete cleanProps.onHide;
    delete cleanProps.removeElement;

    return (
      <div
        ref={this.saveDropdownLink}
        onMouseEnter={showOnHover ? this.onMouseEnter : null}
        onMouseLeave={showOnHover ? this.onMouseLeave : null}
        className={dropdownClasses}
        {...cleanProps}
      >
        {boundChildren}
      </div>
    );
  }
}

Dropdown.propTypes = {
  className: PropTypes.string,
  dropOn: PropTypes.oneOf(['hover', 'click']),
  drop: PropTypes.oneOf([
    'bottom-left',
    'bottom-right',
    'top-left',
    'top-right',
    'right-top',
    'right-bottom',
    'left-top',
    'left-bottom',
  ]),
  disabled: PropTypes.bool,
  children: PropTypes.node,
  removeElement: PropTypes.bool,
  onShow: PropTypes.func,
  onHide: PropTypes.func,
  id: PropTypes.string,
};

Dropdown.defaultProps = {
  className: null,
  dropOn: 'click',
  drop: 'bottom-left',
  disabled: false,
  children: null,
  removeElement: false,
  onShow: noop,
  onHide: noop,
  id: null,
};

export default Dropdown;
